import {Box} from '@mantine/core'
import type {ReactNode} from 'react'
import {useMemo} from 'react'

import {WORKSPACE_SWIM_LANE_BOARD_SURFACE} from '../dispatch/workspace-surfaces'
import {DispatchBoardLaneEmptyMessage} from './board-empty-state'
import {BOARD_LANE_LABELS, BOARD_NEEDS_ACTION_VIEW_LABEL} from './board-lane-labels'
import type {BoardLaneJob} from './board-lanes'
import {splitCleanUp} from './clean-up'
import {CleanUpSection} from './clean-up-section'
import classes from './dispatch.module.css'
import type {TimelineJobRow} from './job-rows'
import {LaneRiskSubheader, laneRiskSubheaderClasses} from './lane-risk-subheader'
import {rankTerminalRows} from './operational-ranking'
import {groupByRiskWindow, type RiskWindowGroup} from './risk-window-groups'
import {resolveNowHour} from './schedule-urgency'
import {laneStickyHeaderClass, stickyClasses} from './swim-lane-sticky'
import {
    expandJobsAcrossDays,
    jobsVisibleInHorizon,
    type TerminalRowEntry,
    todayViewOffset,
} from './terminal-jobs'
import {TerminalRow} from './terminal-row'
import type {ScheduleDate, UnassignedJob, ViewRange} from './types'
import type {DispatchTimelineData} from './types'

type TerminalBoardProps = {
    data: DispatchTimelineData
    viewRange: ViewRange
    onJobPress: (job: UnassignedJob) => void
    onCommittedPress?: (job: TimelineJobRow) => void
    highlightAssignmentId?: string | null
}

function handleRowPress(
    entry: BoardLaneJob,
    onJobPress: (job: UnassignedJob) => void,
    onCommittedPress?: (job: TimelineJobRow) => void,
) {
    if (entry.job.unassignedJob) {
        onJobPress(entry.job.unassignedJob)
        return
    }
    onCommittedPress?.(entry.job)
}

function LaneBlock({
    label,
    count,
    variant,
    children,
}: {
    label: string
    count: number
    variant: 'needs_action' | 'notify_drivers' | 'dispatched'
    children: ReactNode
}) {
    const headerToneClass = {
        needs_action: classes.sectionHeaderNeedsAction,
        notify_drivers: classes.sectionHeaderNotifyDrivers,
        dispatched: classes.sectionHeaderDispatched,
    }[variant]

    const accentClass = {
        needs_action: classes.sectionAccentNeedsAction,
        notify_drivers: classes.sectionAccentNotifyDrivers,
        dispatched: classes.sectionAccentDispatched,
    }[variant]

    const countClass = {
        needs_action: classes.sectionCountNeedsAction,
        notify_drivers: classes.sectionCountNotifyDrivers,
        dispatched: classes.sectionCountDispatched,
    }[variant]

    return (
        <>
            <div
                className={`${classes.sectionHeader} ${headerToneClass} ${laneStickyHeaderClass(variant)}`}
            >
                <div className={classes.sectionHeaderLeading}>
                    <span className={`${classes.sectionAccent} ${accentClass}`} aria-hidden />
                    <span className={classes.sectionLabel}>{label}</span>
                    <span className={`${classes.sectionCount} ${countClass}`}>{count}</span>
                </div>
            </div>
            <div className={classes.laneBody}>{children}</div>
        </>
    )
}

function LaneRiskGroups({
    groups,
    visibleDays,
    viewRange,
    nowHour,
    highlightAssignmentId,
    onPress,
}: {
    groups: RiskWindowGroup[]
    visibleDays: ScheduleDate[]
    viewRange: ViewRange
    nowHour: number
    highlightAssignmentId: string | null
    onPress: (entry: BoardLaneJob) => void
}) {
    // Zebra striping runs continuously down the lane so it does not reset at
    // each risk-window subheader.
    let rowIndex = 0

    return (
        <>
            {groups.map((group) => (
                <div key={group.label ?? 'all'} className={laneRiskSubheaderClasses.group}>
                    {group.label && (
                        <LaneRiskSubheader label={group.label} count={group.entries.length} />
                    )}
                    {group.entries.map((entry) => (
                        <TerminalRow
                            key={`${entry.job.id}-${entry.viewDayOffset}`}
                            entry={entry}
                            visibleDays={visibleDays}
                            viewRange={viewRange}
                            nowHour={nowHour}
                            highlighted={entry.job.id === highlightAssignmentId}
                            alternate={rowIndex++ % 2 === 1}
                            onPress={onPress}
                        />
                    ))}
                </div>
            ))}
        </>
    )
}

function TerminalQueue({
    entries,
    visibleDays,
    viewRange,
    nowHour,
    highlightAssignmentId,
    onJobPress,
    onCommittedPress,
}: {
    entries: TerminalRowEntry[]
    visibleDays: DispatchTimelineData['visibleDays']
    viewRange: ViewRange
    nowHour: number
    highlightAssignmentId: string | null
    onJobPress: (job: UnassignedJob) => void
    onCommittedPress?: (job: TimelineJobRow) => void
}) {
    const days = visibleDays ?? []
    const horizonContext = useMemo(
        () => ({
            visibleDays: days,
            viewRange,
            nowHour,
        }),
        [days, viewRange, nowHour],
    )

    const {current, cleanUp} = splitCleanUp(entries)
    const needsAction = rankTerminalRows(
        current.filter((entry) => entry.lane === 'needs_action'),
        'needs_action',
        horizonContext,
    )
    const notifyDrivers = rankTerminalRows(
        current.filter((entry) => entry.lane === 'notify_drivers'),
        'notify_drivers',
        horizonContext,
    )
    const dispatched = rankTerminalRows(
        current.filter((entry) => entry.lane === 'dispatched'),
        'dispatched',
        horizonContext,
    )
    const press = (entry: BoardLaneJob) => handleRowPress(entry, onJobPress, onCommittedPress)

    const needsActionGroups = groupByRiskWindow(needsAction, viewRange, days)
    const notifyDriversGroups = groupByRiskWindow(notifyDrivers, viewRange, days)
    const dispatchedGroups = groupByRiskWindow(dispatched, viewRange, days)

    return (
        <div className={stickyClasses.laneScrollBody}>
            <LaneBlock
                label={BOARD_NEEDS_ACTION_VIEW_LABEL}
                count={needsAction.length}
                variant="needs_action"
            >
                {needsAction.length > 0 ? (
                    <LaneRiskGroups
                        groups={needsActionGroups}
                        visibleDays={days}
                        viewRange={viewRange}
                        nowHour={nowHour}
                        highlightAssignmentId={highlightAssignmentId}
                        onPress={press}
                    />
                ) : (
                    <DispatchBoardLaneEmptyMessage lane="needs_action" />
                )}
            </LaneBlock>

            <LaneBlock
                label={BOARD_LANE_LABELS.notify_drivers}
                count={notifyDrivers.length}
                variant="notify_drivers"
            >
                {notifyDrivers.length > 0 ? (
                    <LaneRiskGroups
                        groups={notifyDriversGroups}
                        visibleDays={days}
                        viewRange={viewRange}
                        nowHour={nowHour}
                        highlightAssignmentId={highlightAssignmentId}
                        onPress={press}
                    />
                ) : (
                    <DispatchBoardLaneEmptyMessage lane="notify_drivers" />
                )}
            </LaneBlock>

            <LaneBlock
                label={BOARD_LANE_LABELS.dispatched}
                count={dispatched.length}
                variant="dispatched"
            >
                {dispatched.length > 0 ? (
                    <LaneRiskGroups
                        groups={dispatchedGroups}
                        visibleDays={days}
                        viewRange={viewRange}
                        nowHour={nowHour}
                        highlightAssignmentId={highlightAssignmentId}
                        onPress={press}
                    />
                ) : (
                    <DispatchBoardLaneEmptyMessage lane="dispatched" />
                )}
            </LaneBlock>

            <CleanUpSection count={cleanUp.length}>
                {cleanUp.map((entry, index) => (
                    <TerminalRow
                        key={entry.job.id}
                        entry={entry}
                        visibleDays={days}
                        viewRange={viewRange}
                        nowHour={nowHour}
                        highlighted={entry.job.id === highlightAssignmentId}
                        alternate={index % 2 === 1}
                        onPress={press}
                    />
                ))}
            </CleanUpSection>
        </div>
    )
}

export function TerminalBoard({
    data,
    viewRange,
    onJobPress,
    onCommittedPress,
    highlightAssignmentId = null,
}: TerminalBoardProps) {
    const visibleDays = data.visibleDays ?? [data.scheduleDate]
    const dayCount = visibleDays.length
    const nowHour = resolveNowHour()

    const entries = useMemo(() => {
        const jobs = jobsVisibleInHorizon(data)
        const viewDayOriginOffset = data.viewDayOriginOffset ?? 0
        return expandJobsAcrossDays(
            jobs,
            dayCount,
            viewDayOriginOffset,
            todayViewOffset(visibleDays),
        )
    }, [data, dayCount, visibleDays])

    return (
        <Box className={classes.boardShell} style={WORKSPACE_SWIM_LANE_BOARD_SURFACE}>
            <TerminalQueue
                entries={entries}
                visibleDays={visibleDays}
                viewRange={viewRange}
                nowHour={nowHour}
                highlightAssignmentId={highlightAssignmentId}
                onJobPress={onJobPress}
                onCommittedPress={onCommittedPress}
            />
        </Box>
    )
}
